import FutureMedicalCard from "components/Card/FutureMedicalCard";
import { ControlledCheckbox } from "components/Card/ControlledInput";
import { KaboodleToaster } from "components/common";
import LoadingIcon from "components/Loading/LoadingIcon";
import { OCRStatusList } from "components/QuerySection/components/OCRStatusList";
import { PartABForm } from "components/QuerySection/forms/PartABForm";
import { PartCDForm } from "components/QuerySection/forms/PartCDForm";
import { useCoverageData } from "components/QuerySection/hooks/useCoverageData";
import { QuerySectionModals } from "components/QuerySection/components/QuerySectionModals";
import type { FC } from "react";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { usePatchFileByIdMutation } from "services/file-api";
import type {
  AmendedReviewScreeningType,
  CostProjectionDataType,
  MSADataType,
} from "types/DataTypes";

interface FMQueryProps {
  service: MSADataType | CostProjectionDataType | AmendedReviewScreeningType;
  serviceType: string;
}

type CoverageFormValues = {
  coverage_complete: boolean;
  no_coverage: boolean;
};

/**
 * Query section for Future Medical pages
 * Renders coverage checkboxes and plan coverage forms directly
 */
const FMQuery: FC<FMQueryProps> = ({ service }) => {
  const [patchFileById] = usePatchFileByIdMutation();

  const fileId = service?.file?.id;
  const isCoverageComplete = service?.file?.coverage_complete || false;
  const isNoCoverage = service?.file?.no_coverage || false;

  const { allCoverages, isLoading, hasCoverage, hasCoverageValidationErrors } =
    useCoverageData(fileId, isCoverageComplete, isNoCoverage);

  const { control, setValue } = useForm<CoverageFormValues>({
    defaultValues: {
      coverage_complete: isCoverageComplete,
      no_coverage: isNoCoverage,
    },
  });

  useEffect(() => {
    setValue("coverage_complete", isCoverageComplete);
    setValue("no_coverage", isNoCoverage);
  }, [isCoverageComplete, isNoCoverage, setValue]);

  const handleCoverageChange = useCallback(
    async (field: "coverage_complete" | "no_coverage", value: boolean) => {
      if (!fileId) return;

      try {
        await patchFileById({
          id: String(fileId),
          data: { [field]: value },
        }).unwrap();

        const message =
          field === "no_coverage"
            ? value
              ? "Marked as No Coverage"
              : "No Coverage status removed"
            : value
              ? "Coverage marked as complete"
              : "Coverage marked as incomplete";

        KaboodleToaster.success(message);
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
        KaboodleToaster.error(`Failed to update ${field}`);
      }
    },
    [fileId, patchFileById],
  );

  if (!fileId) {
    return null;
  }

  const getMessage = () => {
    if (isCoverageComplete && !hasCoverage) return "Coverage marked complete.";
    if (isNoCoverage) return "No Coverage Selected.";
    if (!hasCoverage) return "No Coverage Information Exists.";
    return null;
  };

  const message = getMessage();

  return (
    <>
      <OCRStatusList fileId={fileId} />

      <div className="swim-column">
        {/* Query Information Card */}
        <FutureMedicalCard heading="Query Information">
          <ControlledCheckbox
            control={control}
            name="coverage_complete"
            label="Coverage Complete"
            onSave={(value) => handleCoverageChange("coverage_complete", value)}
            readOnly={hasCoverageValidationErrors}
            disabledReason={
              hasCoverageValidationErrors
                ? "Please resolve all validation errors on the Plan Coverage Information."
                : undefined
            }
          />
          <ControlledCheckbox
            control={control}
            name="no_coverage"
            label="No Coverage"
            onSave={(value) => handleCoverageChange("no_coverage", value)}
            readOnly={hasCoverage}
            disabledReason={
              hasCoverage
                ? "Cannot mark as no coverage if coverage already exists."
                : undefined
            }
          />
        </FutureMedicalCard>

        {/* Coverage Forms */}
        {isLoading ? (
          <LoadingIcon message="Loading coverage information..." />
        ) : message ? (
          <FutureMedicalCard heading="Coverage Information">
            <p className="text-center">{message}</p>
          </FutureMedicalCard>
        ) : (
          allCoverages.map((coverage) =>
            coverage.coverageType === "AB" ? (
              <PartABForm
                key={coverage.id}
                coverage={coverage}
                isComplete={isCoverageComplete}
              />
            ) : (
              <PartCDForm
                key={coverage.id}
                coverage={coverage}
                isComplete={isCoverageComplete}
              />
            ),
          )
        )}
      </div>

      {/* Modals */}
      <QuerySectionModals fileId={fileId} organization={service.organization} />
    </>
  );
};

export default FMQuery;
